import { useState, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography, theme as themeColors } from '../theme/colors';
import axiosInstance from '../lib/axios';
import type { ProductImage } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface ImageUploadProps {
  productId: string;
  images: ProductImage[];
  onImagesChange: (images: ProductImage[]) => void;
}

interface SortableImageProps {
  image: ProductImage;
  index: number;
  colors: any;
  deleting: string | null;
  onDelete: (image: ProductImage) => void;
  getImageUrl: (image: ProductImage) => string;
}

const SortableImage = ({
  image,
  index,
  colors,
  deleting,
  onDelete,
  getImageUrl,
}: SortableImageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: themeColors.radius.lg,
        overflow: 'hidden',
        border: `1px solid ${isDragging ? palette.primary : colors.border}`,
        backgroundColor: colors.bgElevated,
      }}
    >
      <Box
        component="img"
        src={getImageUrl(image)}
        alt={image.originalName || `Product image ${index + 1}`}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        onError={(e: any) => {
          e.target.style.display = 'none';
        }}
      />

      {/* Drag handle */}
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        sx={{
          position: 'absolute',
          top: 4,
          left: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          cursor: 'grab',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </IconButton>

      {/* Delete button */}
      <IconButton
        size="small"
        onClick={() => onDelete(image)}
        disabled={deleting === image.id}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          '&:hover': {
            backgroundColor: palette.error,
          },
        }}
      >
        {deleting === image.id ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>

      {/* Image number badge */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 4,
          left: 4,
          backgroundColor: index === 0 ? palette.primary : 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          px: 1,
          py: 0.25,
          borderRadius: themeColors.radius.sm,
          fontSize: '10px',
          fontWeight: typography.fontWeight.semibold,
        }}
      >
        {index === 0 ? 'Main' : `#${index + 1}`}
      </Box>
    </Box>
  );
};

export const ImageUpload = ({ productId, images, onImagesChange }: ImageUploadProps) => {
  const { mode } = useContext(ColorModeContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const colors = mode === 'dark' ? {
    bg: palette.surface,
    bgElevated: palette.surfaceElevated,
    bgHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    text: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.surface,
    bgElevated: palette.light.surfaceElevated,
    bgHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    text: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const getImageUrl = (image: ProductImage) => {
    // Use medium size for display, prepend API URL
    const path = image.medium || image.thumbnail || image.large;
    if (!path) return '';
    if (path.startsWith('http')) {
      return path;
    }
    // Remove leading slash to avoid double slashes in URL
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${API_URL}/${cleanPath}`;
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axiosInstance.post(`/products/${productId}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onImagesChange(response.data.images);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (image: ProductImage) => {
    setDeleting(image.id);
    setError(null);

    try {
      const response = await axiosInstance.delete(`/products/${productId}/images/${image.id}`);
      onImagesChange(response.data.images);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete image');
    } finally {
      setDeleting(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = images.findIndex((img) => img.id === active.id);
    const newIndex = images.findIndex((img) => img.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update the UI
    const newImages = arrayMove(images, oldIndex, newIndex);
    onImagesChange(newImages);

    // Save the new order to the backend
    setReordering(true);
    setError(null);

    try {
      const imageIds = newImages.map((img) => img.id);
      await axiosInstance.patch(`/products/${productId}/images/reorder`, {
        imageIds,
      });
    } catch (err: any) {
      // Revert on error
      onImagesChange(images);
      setError(err.response?.data?.message || 'Failed to reorder images');
    } finally {
      setReordering(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: typography.fontWeight.semibold,
            color: colors.text,
          }}
        >
          Product Images
        </Typography>
        {reordering && (
          <CircularProgress size={14} sx={{ color: palette.primary }} />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={images.map((img) => img.id)} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 2,
            }}
          >
            {/* Sortable Images */}
            {images.map((image, index) => (
              <SortableImage
                key={image.id}
                image={image}
                index={index}
                colors={colors}
                deleting={deleting}
                onDelete={handleDeleteImage}
                getImageUrl={getImageUrl}
              />
            ))}

            {/* Upload Button */}
            <Box
              onClick={handleUploadClick}
              sx={{
                aspectRatio: '1',
                borderRadius: themeColors.radius.lg,
                border: `2px dashed ${colors.border}`,
                backgroundColor: colors.bg,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: themeColors.transitions.fast,
                '&:hover': uploading ? {} : {
                  borderColor: palette.primary,
                  backgroundColor: colors.bgHover,
                },
              }}
            >
              {uploading ? (
                <CircularProgress size={24} sx={{ color: palette.primary }} />
              ) : (
                <>
                  <AddPhotoAlternateOutlinedIcon
                    sx={{ color: colors.textMuted, fontSize: 32, mb: 0.5 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontSize: '11px',
                      textAlign: 'center',
                    }}
                  >
                    Add Image
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </SortableContext>
      </DndContext>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Help text */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 1.5,
          color: colors.textMuted,
          fontSize: '11px',
        }}
      >
        Drag images to reorder. First image is the main product image. Supported: JPG, PNG, GIF, WebP (max 5MB).
      </Typography>
    </Box>
  );
};
